import {Spec} from 'immutability-helper';

import {assert} from 'Asserts';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import areNotePartsEqual from 'notebook/notes/util/areNotePartsEqual';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {CrossTableCellColors} from 'notebook/tables/constants/CellConstants';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {ExternalProps} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import {isStructuredTable} from 'notebook/TypePredicates';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';

import {
  NoteLinkedGroup,
  StructuredTableSpreadsheetProps,
  UnknownNoteLinkedGroup,
} from '../NoteLinkedGroupConstants';
import {NoteLinkedGroupIsolatedViewProps} from '../NoteLinkedGroupView';
import maybeAsyncUpdateContainedNotePart from './maybeAsyncUpdateContainedNotePart';
import {isMetadataForRestoreForContainedNotePart} from './NoteLinkedGroupSelection';

// ---------------------------------------------------------------------------
// Entry-level evaluation path: reads computedTables from editor.props
// ---------------------------------------------------------------------------

export function makeTableItemPropsByName(
  group: UnknownNoteLinkedGroup,
  editor: NoteEditor,
  spreadsheetProps: StructuredTableSpreadsheetProps,
  cellColors: CrossTableCellColors,
  editingTable: string | null,
  computedTables: OutputTableMap,
  // Passed in explicitly (rather than read off `editor.props` inside this function) so the
  // caller's useMemo can list it as a dependency. `editor` is a stable reference, so reading
  // `showExcessLabels` from `editor.props` here would leave the memoized result stale when it
  // toggles on its own (e.g. as a formula edit starts).
  showExcessLabels: boolean
): {
  tableItemPropsByName: {[tableName: string]: TableItemProps};
  evaluatedTableMap: OutputTableMap;
} {
  const evaluatedTableMap: OutputTableMap = {};
  const tableItemPropsByName: {[tableName: string]: TableItemProps} = {};
  const {tableNamespace, selectorCounts} = editor.props;
  const selectors = editor.props.selectorsFromProps({note: editor.props.note});

  group.containedNoteParts.forEach((containedNotePart: NotePart) => {
    if (!containedNotePart.item) {
      return;
    }
    if (!isStructuredTable(containedNotePart.item)) {
      return;
    }
    const structuredTable = containedNotePart.item as UnknownStructuredTable;
    const table = getStructuredTableAdapters()[structuredTable.kind].toTable(structuredTable);
    const computedTable = computedTables[table.name];
    const validationSnapshot = editor.getValidationErrorSnapshot();

    const tableItemProps: TableItemProps = {
      tableNamespace,
      showExcessLabels,
      computedTable,
      validationErrors: validationSnapshot[table.name] ?? [],
      table,
      cellColors: (cellColors[table.name] ?? {}) as {[rowIndex: number]: {[columnIndex: number]: string}},
      isMaximized: false,
      isSplitScreen: false,
      isFocused: false,
      multiEditing: editingTable === table.name,
      selectors,
      selectorCounts,
      metadataForRestore: isMetadataForRestoreForContainedNotePart(
        spreadsheetProps.metadataForRestore,
        containedNotePart
      )
        ? spreadsheetProps.metadataForRestore
        : null,
    };
    tableItemPropsByName[table.name] = tableItemProps;
    evaluatedTableMap[table.name] = computedTable;
  });
  return {
    tableItemPropsByName,
    evaluatedTableMap,
  };
}

// ---------------------------------------------------------------------------
// Isolated evaluation path (HOC): reads computedTables from HOC-injected props
// ---------------------------------------------------------------------------

export function makeTableItemPropsByNameForIsolatedEval(props: NoteLinkedGroupIsolatedViewProps): {
  tableItemPropsByName: {[tableName: string]: TableItemProps};
  evaluatedTableMap: OutputTableMap;
} {
  const evaluatedTableMap: OutputTableMap = {};
  const tableItemPropsByName: {[tableName: string]: TableItemProps} = {};
  props.group.containedNoteParts.forEach((containedNotePart: NotePart) => {
    if (!containedNotePart.item) {
      return;
    }
    if (!isStructuredTable(containedNotePart.item)) {
      return;
    }
    const structuredTable = containedNotePart.item as UnknownStructuredTable;
    const table = getStructuredTableAdapters()[structuredTable.kind].toTable(structuredTable);
    const computedTable = props.computedTables[table.name];
    const validationSnapshot = props.editor.getValidationErrorSnapshot();

    const tableItemProps: TableItemProps = {
      tableNamespace: props.tableNamespace,
      showExcessLabels: props.showExcessLabels,
      computedTable,
      validationErrors: validationSnapshot[table.name] ?? [],
      table,
      cellColors: props.cellColors as {[rowIndex: number]: {[columnIndex: number]: string}},
      isMaximized: false,
      isSplitScreen: false,
      isFocused: false,
      multiEditing: props.editingTable === table.name,
      selectors: props.selectorsFromProps(props),
      selectorCounts: props.selectorCounts,
      metadataForRestore: isMetadataForRestoreForContainedNotePart(
        props.spreadsheetProps.metadataForRestore,
        containedNotePart
      )
        ? props.spreadsheetProps.metadataForRestore
        : null,
    };
    tableItemPropsByName[table.name] = tableItemProps;
    evaluatedTableMap[table.name] = computedTable;
  });
  return {
    tableItemPropsByName,
    evaluatedTableMap,
  };
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function _makeFullTableMapForGroup<TGroupMetadata>(
  group: NoteLinkedGroup<TGroupMetadata>
): FullTableMap {
  const fullTableMap: FullTableMap = {};
  group.containedNoteParts.forEach((containedNotePart) => {
    if (!containedNotePart.item) {
      return;
    }
    if (!isStructuredTable(containedNotePart.item)) {
      return;
    }
    const structuredTable = containedNotePart.item;
    const table = getStructuredTableAdapters()[structuredTable.kind].toTable(structuredTable);
    fullTableMap[table.name] = table;
  });
  return fullTableMap;
}

function findModifiedNotePart(
  prevGroup: NoteLinkedGroup<unknown>,
  nextGroup: NoteLinkedGroup<unknown>
): NotePart | null {
  // The spreadsheet evaluation system returns the entire updated object,
  // so as a hack we figure out which NotePart was actually modified.
  // We know this should be single item since (cross-table) formulas are disabled.
  const modifiedNps = nextGroup.containedNoteParts.filter((nextNp) => {
    const matchingPrevNp = prevGroup.containedNoteParts.find((np) => np.cid === nextNp.cid);
    assert(matchingPrevNp != null, 'expected all nps to still exist');
    return !areNotePartsEqual(nextNp, matchingPrevNp);
  });
  if (modifiedNps.length === 1) {
    return modifiedNps[0];
  } else {
    assert(modifiedNps.length === 0, 'too many modifications!');
    return null;
  }
}

export function getExternalPropsForSpreadsheetEval(
  editorProps: NoteEditorProps<NotePart>,
  group: UnknownNoteLinkedGroup,
  editor: NoteEditor,
  notePartCid: string
): ExternalProps<NotePart | UnknownNoteLinkedGroup, Note | UnknownNoteLinkedGroup> {
  const {getContent, getType, getCid, setContent, requestSelection, extraUsedTableNames, spreadsheetTypes} =
    editorProps;
  return {
    // Inherited from editorProps:
    getContent,
    getType,
    getCid,
    setContent,
    requestSelection,
    extraUsedTableNames,
    spreadsheetTypes,
    // Custom behavior for NoteLinkedGroups (isolated spreadsheet environment without formulas):
    getItems: (nextGroup: UnknownNoteLinkedGroup) => nextGroup.containedNoteParts,
    getRoot: () => group,
    itemsAccessor: (op: {[i: number]: Spec<NotePart>}) => ({containedNoteParts: op}),
    makeFullTableMap: (nextProps: NoteLinkedGroupIsolatedViewProps) =>
      _makeFullTableMapForGroup(nextProps.group as NoteLinkedGroup<unknown>),
    selectorsFromProps: () => [],
    spreadsheetRoot: ((nextProps: NoteLinkedGroupIsolatedViewProps) =>
      nextProps.group.containedNoteParts) as unknown as (props: unknown) => UnknownNoteLinkedGroup,
    updateRoot: async (nextGroup: UnknownNoteLinkedGroup) => {
      const notePart = findModifiedNotePart(group, nextGroup);
      if (!notePart) {
        return;
      }
      await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, notePart);
    },
    disallowCellRefs: true,
    getNoteRuntimeSnapshot: () =>
      // NoteLinkedGroup spreadsheet "evaluation" is currently done live as it does not yet support formulas.
      null,
    fileId: editorProps.fileId,
  };
}
